import React, { useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FuseScrollbars } from '@fuse';
import clsx from 'clsx';
import FusePageSimpleSidebar from './FusePageSimpleSidebar';
import PropTypes from 'prop-types'
import MessageToUser from '../../../../app/components/MessageToUser';
import { usePlace } from 'commons/usePlace';
import { reverse } from 'ramda';

const headerHeight = 120;
const toolbarHeight = 64;
const drawerWidth = 240;

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    position: 'relative',
    flex: '1 0 auto',
    height: props => (props.isFitViewPort ? '100%' : 'auto'),
    backgroundColor: theme.palette.background.default,
  },
  innerScroll: {
    flex: '1 1 auto',
    height: '100%',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    zIndex: 2,
    maxWidth: '100%',
    minWidth: 0,
    height: '100%',
    backgroundColor: theme.palette.background.default,
  },
  header: {
    height: headerHeight,
    minHeight: headerHeight,
    display: 'flex',
    background: theme.palette.primary.background,
    color: theme.palette.primary.main,
    backgroundSize: 'cover',
    backgroundColor: theme.palette.primary.background,
  },
  topBg: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: headerHeight,
    pointerEvents: 'none',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    zIndex: 9999,
  },
  toolbar: {
    height: toolbarHeight,
    minHeight: toolbarHeight,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
  },
  content: ({ isFitViewPort }) => ({
    flex: isFitViewPort ? undefined : '1 0 auto',
    overflow: isFitViewPort ? 'hidden' : undefined,
  }),
  sidebarWrapper: {
    overflow: 'hidden',
    backgroundColor: 'transparent',
    position: 'absolute',
    '&.permanent': {
      [theme.breakpoints.up('lg')]: {
        position: 'relative',
      },
    },
  },
  sidebar: {
    position: 'absolute',
    '&.permanent': {
      [theme.breakpoints.up('lg')]: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        position: 'relative',
      },
    },
    width: drawerWidth,
    height: '100%',
  },
  leftSidebar: {
    [theme.breakpoints.up('lg')]: {
      borderRight: '1px solid ' + theme.palette.divider,
      borderLeft: 0,
    },
  },
  rightSidebar: {
    [theme.breakpoints.up('lg')]: {
      borderLeft: '1px solid ' + theme.palette.divider,
      borderRight: 0,
    },
  },
  sidebarHeader: {
    height: headerHeight,
    minHeight: headerHeight,
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
  },
  sidebarHeaderInnerSidebar: {
    backgroundColor: 'transparent',
    color: 'inherit',
    height: 'auto',
    minHeight: 'auto',
  },
  sidebarContent: {},
  backdrop: {
    position: 'absolute',
  },
}));

const FusePageSimple = React.forwardRef(function (props, ref) {
  const leftSidebarRef = useRef(null);
  const rightSidebarRef = useRef(null);
  const rootRef = useRef(null);
  const classes = useStyles(props);
  const isFitViewPort = props.isFitViewPort === true;
  const isDisplayMessage = props.isDisplayMessage;
  const place = usePlace();

  React.useImperativeHandle(ref, () => {
    return {
      rootRef: rootRef,
      toggleLeftSidebar: () => {
        leftSidebarRef.current.toggleSidebar();
      },
      toggleRightSidebar: () => {
        rightSidebarRef.current.toggleSidebar();
      },
    };
  });

  let message, messagesCount;
  let dismissedMessagesCount = 0;

  if (isDisplayMessage) {
    message = place.messages && reverse(place.messages)[0];
    messagesCount = place.messagesCount;
    if (place.messages) {
      dismissedMessagesCount = place.messagesCount - place.messages.length + 1;
    }
  }
  return (
    <div
      className={clsx(
        'overflow-auto h-full',
        isFitViewPort && 'min-h-full flex flex-col flex-auto'
      )}
      id="scrollableDiv"
    >
      <div
        className={clsx(
          classes.root,
          props.innerScroll && classes.innerScroll,
          isFitViewPort && 'flex-auto'
        )}
        ref={rootRef}
      >
        <div className={clsx(classes.header, classes.topBg)} />

        <div
          className={clsx(
            'flex flex-auto flex-col container z-10',
            isFitViewPort && 'overflow-hidden'
          )}
        >      
          <div className={clsx(classes.wrapper,'bg-primary-100')}>
            {(props.leftSidebarHeader || props.leftSidebarContent) && (
              <FusePageSimpleSidebar
                position="left"
                header={props.leftSidebarHeader}
                content={props.leftSidebarContent}
                variant={props.leftSidebarVariant || 'permanent'}
                innerScroll={props.innerScroll}
                sidebarInner={props.sidebarInner}
                classes={classes}
                ref={leftSidebarRef}
                rootRef={rootRef}
              />
            )}

            <FuseScrollbars
              className={classes.contentWrapper}
              enable={props.innerScroll && !props.sidebarInner}
            >
              {!!message && (
                <MessageToUser
                  classes={classes}
                  content={message}
                  messagesTotalCount={messagesCount}
                  dismissedMessagesCount={dismissedMessagesCount}
                  placeId={place.id}
                />
              )}

              {props.contentToolbar && (
                <div className={classes.toolbar}>{props.contentToolbar}</div>
              )}

              {props.content && <div className={classes.content}>{props.content}</div>}
            </FuseScrollbars>

            {(props.rightSidebarHeader || props.rightSidebarContent) && (
              <FusePageSimpleSidebar
                position="right"
                header={props.rightSidebarHeader}
                content={props.rightSidebarContent}
                variant={props.rightSidebarVariant || 'permanent'}
                innerScroll={props.innerScroll}
                sidebarInner={props.sidebarInner}
                classes={classes}
                ref={rightSidebarRef}
                rootRef={rootRef}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

FusePageSimple.displayName = 'FusePageSimple';

FusePageSimple.propTypes = {
  leftSidebarHeader: PropTypes.node,
  leftSidebarContent: PropTypes.node,
  leftSidebarVariant: PropTypes.node,
  rightSidebarHeader: PropTypes.node,
  rightSidebarContent: PropTypes.node,
  rightSidebarVariant: PropTypes.node,
  header: PropTypes.node,
  content: PropTypes.node,
  contentToolbar: PropTypes.node,
  sidebarInner: PropTypes.bool,
  innerScroll: PropTypes.bool,
};

export default React.memo(FusePageSimple);
